import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON, postJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { HIRING_V2_SLICE_NAME } from 'features/hiringV2/constants';
import { createReducers, createSelectors } from 'features/hiringV2/helpers';

import {
  CreateOAMCalendarPayload,
  HiringNylasCalender,
  NylasRequirementsFromToken,
  UpdateOAMCalendarPayload,
} from '../../types/nylas';

import { INITIAL_CALENDAR_STATE } from './constants';
import { CalendarState } from './types';

const SLICE_NAME = 'calendar';

const initialState = INITIAL_CALENDAR_STATE;

const calendarSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: createReducers(initialState),
});

const getCalendarState = (state: RootState) =>
  state.get(HIRING_V2_SLICE_NAME).calendar as CalendarState;

// Selectors
export const selectCalendar = createSelectors(getCalendarState, initialState);
export const calendarActions = calendarSlice.actions;

// THUNKS
export const getOAMCalendar = createAsyncThunk(
  `${SLICE_NAME}/getOAMCalendar`,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const calendar_ = selectCalendar.selectOamCalendar(state);
    if (calendar_ && calendar_.id) {
      return calendar_;
    }
    try {
      dispatch(calendarActions.setOamCalendarLoading(true));
      const response = (await fetchJSON(
        `/hiring/v2/calendars/primary.json`
      )) as {
        calendar: HiringNylasCalender;
      };
      dispatch(calendarActions.setOamCalendar(response.calendar));
      dispatch(calendarActions.setOamCalendarError(null));
      return response.calendar;
    } catch (err) {
      const error = err as Error;
      dispatch(calendarActions.setOamCalendarError(error.message));
      throw error;
    } finally {
      dispatch(calendarActions.setOamCalendarLoading(false));
    }
  }
);

export const createOAMCalendar = createAsyncThunk<
  HiringNylasCalender,
  CreateOAMCalendarPayload,
  { rejectValue: unknown; state: RootState }
>(
  `${SLICE_NAME}/createOAMCalendar`,
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = (await postJSON(`/hiring/v2/calendars`, {
        options: params,
      })) as {
        calendar: HiringNylasCalender;
      };
      dispatch(calendarActions.setOamCalendar(response.calendar));
      dispatch(calendarActions.setOamCalendarError(null));
      return response.calendar;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateOAMCalendar = createAsyncThunk<
  HiringNylasCalender,
  UpdateOAMCalendarPayload,
  { rejectValue: unknown; state: RootState }
>(
  `${SLICE_NAME}/updateOAMCalendar`,
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = (await putJSON(`/hiring/v2/calendars/${params.id}`, {
        options: params,
      })) as {
        calendar: HiringNylasCalender;
      };
      dispatch(calendarActions.setOamCalendar(response.calendar));
      dispatch(calendarActions.setOamCalendarError(null));
      return response.calendar;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createNylasSchedulerSessionId = createAsyncThunk(
  `${SLICE_NAME}/createNylasSchedulerSessionId`,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const calendar_ = selectCalendar.selectOamCalendar(state);
    if (!calendar_ || !calendar_.id) {
      await dispatch(getOAMCalendar()).unwrap();
    }
    try {
      const calendar = selectCalendar.selectOamCalendar(state);
      dispatch(calendarActions.setSessionIdLoading(true));
      const response = (await postJSON(
        `hiring/v2/calendars/${calendar?.id}/create_scheduling_session`
      )) as {
        session_id: string;
      };
      dispatch(calendarActions.setSessionId(response.session_id));
      dispatch(calendarActions.setSessionIdTimestamp(Date.now()));
      dispatch(calendarActions.setSessionIdError(null));
      return response.session_id;
    } catch (err) {
      const error = err as Error;
      dispatch(calendarActions.setSessionIdError(error.message));
      throw error;
    } finally {
      dispatch(calendarActions.setSessionIdLoading(false));
    }
  }
);

export const checkSessionIdTimestamp = createAsyncThunk(
  `${SLICE_NAME}/checkSessionIdTimestamp`,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const calendar_ = selectCalendar.selectOamCalendar(state);

    if (!calendar_ || !calendar_.id) {
      await dispatch(getOAMCalendar()).unwrap();
    }

    const sessionIdTimestamp = selectCalendar.selectSessionIdTimestamp(state);
    const now = Date.now();
    // Session Validity is 30 minutes, so threshold is set to 25 minutes for allowance
    const REFRESH_THRESHOLD = 25 * 60 * 1000;

    const isExpired =
      !sessionIdTimestamp || now - sessionIdTimestamp > REFRESH_THRESHOLD;

    if (!isExpired) {
      return selectCalendar.selectSessionId(state);
    }

    await dispatch(createNylasSchedulerSessionId()).unwrap();

    const newState = getState(); // important: get fresh state
    return selectCalendar.selectSessionId(newState);
  }
);

export const getNylasRequirementsFromToken = createAsyncThunk(
  `${SLICE_NAME}/getNylasRequirementsFromToken`,
  async (token: string, { rejectWithValue }) => {
    try {
      const response = (await fetchJSON(
        `/hiring/v2/scheduling_sessions/${token}`
      )) as NylasRequirementsFromToken;
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message);
    }
  }
);

export const { reducer, actions } = calendarSlice;
